import React, { useState } from "react";
import { ToggleSwitch } from "../../../shared";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import type { ResultParamField } from "../constants";
import styles from "../stylesDynamicBonuses.module.css";

export type RewardSizingMode = "amount" | "percent";

/**
 * BONENG-1171 — pill/cap choice is authoring state, not a form value.
 * Reset during render when `resetKey` changes so the previous type's mode
 * never paints on the new result (React retries the render before commit).
 */
export const useRewardSizingAuthoring = (resetKey: string) => {
  const [selectedSizingMode, setSelectedSizingMode] =
    useState<RewardSizingMode | null>(null);
  const [sizingCapOpen, setSizingCapOpen] = useState<boolean | null>(null);
  const [prevResetKey, setPrevResetKey] = useState(resetKey);
  if (resetKey !== prevResetKey) {
    setPrevResetKey(resetKey);
    setSelectedSizingMode(null);
    setSizingCapOpen(null);
  }
  return {
    selectedSizingMode,
    setSelectedSizingMode,
    sizingCapOpen,
    setSizingCapOpen,
  };
};

type AssembleRewardSizingGridArgs = {
  gridParams: ResultParamField[];
  renderedParams: React.ReactNode[];
  showSizingModes: boolean;
  sizingMode: RewardSizingMode;
  capVisible: boolean;
  fixedAmountParam?: ResultParamField;
  percentParam?: ResultParamField;
  capParam?: ResultParamField;
  onSelectMode: (mode: RewardSizingMode) => void;
  onToggleCap: (next: boolean) => void;
};

/**
 * Reorder already-rendered result-param Form.Items into the BONENG-1171
 * sizing selector (one mode at a time, optional cap) plus the leftover
 * fields. When the selector is off, returns the original list unchanged.
 */
export const assembleRewardSizingGrid = ({
  gridParams,
  renderedParams,
  showSizingModes,
  sizingMode,
  capVisible,
  fixedAmountParam,
  percentParam,
  capParam,
  onSelectMode,
  onToggleCap,
}: AssembleRewardSizingGridArgs): React.ReactNode => {
  if (!showSizingModes || !fixedAmountParam || !percentParam) {
    return renderedParams;
  }

  const renderedByKey = new Map(
    gridParams.map((param, index) => [param.key, renderedParams[index]]),
  );
  const sizingKeys = new Set(
    [fixedAmountParam.key, percentParam.key, capParam?.key].filter(
      (key): key is string => Boolean(key),
    ),
  );

  const modes: Array<{ mode: RewardSizingMode; label: string }> = [
    {
      mode: "amount",
      label: AppStrings.PAM_DYNAMIC_BONUS_SIZING_FIXED_AMOUNT,
    },
    {
      mode: "percent",
      label: AppStrings.PAM_DYNAMIC_BONUS_PARAM_PERCENT_OF_TRIGGER_AMOUNT,
    },
  ];

  return (
    <>
      <div className={styles.rewardSizing} data-testid="reward-sizing">
        <div
          role="radiogroup"
          aria-label={AppStrings.PAM_DYNAMIC_BONUS_SIZING_MODE_LABEL}
          className={styles.pillGroup}
        >
          {modes.map(({ mode, label }) => (
            <button
              key={mode}
              type="button"
              role="radio"
              aria-checked={sizingMode === mode}
              onClick={() => onSelectMode(mode)}
              onKeyDown={(event) => {
                if (
                  event.key !== "ArrowLeft" &&
                  event.key !== "ArrowRight" &&
                  event.key !== "ArrowUp" &&
                  event.key !== "ArrowDown"
                ) {
                  return;
                }
                event.preventDefault();
                onSelectMode(
                  event.key === "ArrowRight" || event.key === "ArrowDown"
                    ? "percent"
                    : "amount",
                );
              }}
              className={[
                styles.pill,
                sizingMode === mode ? styles.pillSelected : "",
              ]
                .join(" ")
                .trim()}
            >
              {label}
            </button>
          ))}
        </div>
        {sizingMode === "amount" ? (
          renderedByKey.get(fixedAmountParam.key)
        ) : (
          <>
            {renderedByKey.get(percentParam.key)}
            {percentParam.percentHint && (
              <p className={styles.rewardSizingHint}>
                {percentParam.percentHint}
              </p>
            )}
            {capParam && (
              <>
                <div className={styles.rewardCapRow}>
                  <ToggleSwitch
                    tone="info"
                    value={capVisible}
                    onChange={onToggleCap}
                    ariaLabel={AppStrings.PAM_DYNAMIC_BONUS_CAP_THE_AMOUNT}
                  />
                  <span className={styles.rewardCapLabel}>
                    {AppStrings.PAM_DYNAMIC_BONUS_CAP_THE_AMOUNT}
                  </span>
                  <span className={styles.rewardCapOptional}>
                    {AppStrings.PAM_DYNAMIC_BONUS_CAP_OPTIONAL}
                  </span>
                </div>
                {capVisible && renderedByKey.get(capParam.key)}
              </>
            )}
          </>
        )}
      </div>
      {gridParams
        .filter((param) => !sizingKeys.has(param.key))
        .map((param) => (
          <React.Fragment key={param.key}>
            {renderedByKey.get(param.key)}
          </React.Fragment>
        ))}
    </>
  );
};
